
import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface DatePickerProps {
  date?: Date
  setDate?: (date?: Date) => void
  placeholder?: string
  mode?: "single" | "multiple" | "range"
  selected?: Date | Date[] | { from: Date; to: Date }
  onSelect?: (date?: Date | Date[] | { from: Date; to: Date }) => void
  placeholderText?: string
  className?: string
  disabled?: boolean
}

export function DatePicker({
  date,
  setDate,
  placeholder = "Pick a date",
  mode = "single",
  selected,
  onSelect,
  placeholderText = "Pick a date",
  className,
  disabled = false,
  ...props
}: DatePickerProps) {
  // Handle both direct date state and controlled mode
  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (setDate) {
      setDate(selectedDate);
    }
    
    if (onSelect) {
      onSelect(selectedDate);
    }
  };

  // Determine which selected and onSelect to use
  const effectiveSelected = selected || date;
  const effectiveOnSelect = onSelect || handleDateSelect;
  const displayPlaceholder = placeholderText || placeholder;

  // Helper to get display text for the selected date
  const getDisplayText = () => {
    if (!effectiveSelected) {
      return <span>{displayPlaceholder}</span>;
    }
    
    if (effectiveSelected instanceof Date) {
      return format(effectiveSelected, "PPP");
    }
    
    if (Array.isArray(effectiveSelected)) {
      return effectiveSelected.length > 0 
        ? `${effectiveSelected.length} selected` 
        : <span>{displayPlaceholder}</span>;
    }
    
    if (typeof effectiveSelected === 'object' && 'from' in effectiveSelected) {
      if (effectiveSelected.to) {
        return `${format(effectiveSelected.from, "PPP")} - ${format(effectiveSelected.to, "PPP")}`;
      }
      return `${format(effectiveSelected.from, "PPP")} - ...`;
    }
    
    return <span>{displayPlaceholder}</span>;
  };

  return (
    <Popover>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          variant={"outline"}
          className={cn(
            "w-full justify-start text-left font-normal",
            !effectiveSelected && "text-muted-foreground",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {getDisplayText()}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {mode === "single" && (
          <Calendar
            mode="single"
            selected={effectiveSelected as Date}
            onSelect={effectiveOnSelect as (date?: Date) => void}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
          />
        )}
        {mode === "multiple" && (
          <Calendar
            mode="multiple"
            selected={effectiveSelected as Date[]}
            onSelect={effectiveOnSelect as (dates?: Date[]) => void}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
          />
        )}
        {mode === "range" && (
          <Calendar
            mode="range"
            selected={effectiveSelected as { from: Date; to: Date }}
            onSelect={effectiveOnSelect as (range?: { from: Date; to: Date }) => void}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
          />
        )}
      </PopoverContent>
    </Popover>
  )
}
